import { Injectable, Inject, PLATFORM_ID, NgZone } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

export interface ModalRegistration {
  id: any;
  onClose?: () => void;
  closeOnEsc?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class ModalService {
  private activeModals: ModalRegistration[] = [];
  private observedModalElements = new Set<HTMLElement>();
  private isBrowser: boolean;
  private savedDocOverflow: string = '';
  private savedDocOverscroll: string = '';
  private savedBodyOverflow: string = '';
  private savedBodyOverscroll: string = '';
  private savedBodyPaddingRight: string = '';
  private savedElementsPadding: Map<HTMLElement, string> = new Map();
  private scrollListenersAdded = false;
  private isLocked = false;
  private mutationObserver?: MutationObserver;
  private rafId: number | null = null;
  private autoIdCounter = 0;
  private touchStartY = 0;
  private touchStartX = 0;

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    private ngZone: NgZone
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);
    if (this.isBrowser) {
      this.initDomObserver();
    }
  }

  /**
   * Registers a modal opening and locks body scrolling.
   * @param id Unique identifier for the modal (string, object, component instance).
   * @param onClose Optional callback triggered when ESC key is pressed while this modal is active.
   * @param closeOnEsc Whether ESC key should close this modal (defaults to true).
   * @returns Cleanup function that closes this modal.
   */
  openModal(id?: any, onClose?: () => void, closeOnEsc = true): () => void {
    if (!this.isBrowser) {
      return () => {};
    }

    const modalId = id ?? `modal_${++this.autoIdCounter}`;

    const existingIndex = this.activeModals.findIndex(m => m.id === modalId);
    if (existingIndex !== -1) {
      this.activeModals[existingIndex] = { id: modalId, onClose, closeOnEsc };
      return () => this.closeModal(modalId);
    }

    this.activeModals.push({ id: modalId, onClose, closeOnEsc });
    this.syncScrollLockState();

    return () => this.closeModal(modalId);
  }

  /**
   * Closes / deregisters a modal by its ID and restores scrolling if all modals are closed.
   * If no ID is passed, removes the topmost modal.
   */
  closeModal(id?: any): void {
    if (!this.isBrowser) return;

    if (id !== undefined) {
      const index = this.activeModals.findIndex(m => m.id === id);
      if (index !== -1) {
        this.activeModals.splice(index, 1);
      }
    } else {
      this.activeModals.pop();
    }

    this.syncScrollLockState();
  }

  /**
   * Force closes all modal registrations and restores body scrolling.
   */
  closeAllModals(): void {
    if (!this.isBrowser) return;
    this.activeModals = [];
    this.observedModalElements.clear();
    this.syncScrollLockState();
  }

  /**
   * Checks whether any modal is currently open.
   */
  isModalOpen(): boolean {
    return this.activeModals.length > 0 || this.observedModalElements.size > 0;
  }

  /**
   * Gets total number of open modals (explicit + observed).
   */
  get modalCount(): number {
    return this.activeModals.length + this.observedModalElements.size;
  }

  /**
   * Synchronizes the body and document scroll lock state based on active & observed modal counts.
   */
  private syncScrollLockState(): void {
    const shouldLock = this.isModalOpen();

    if (shouldLock && !this.isLocked) {
      this.lockBodyScroll();
      this.attachScrollListeners();
      this.isLocked = true;
    } else if (!shouldLock && this.isLocked) {
      this.unlockBodyScroll();
      this.detachScrollListeners();
      this.isLocked = false;
    }
  }

  private lockBodyScroll(): void {
    const body = document.body;
    const docEl = document.documentElement;

    this.savedDocOverflow = docEl.style.overflow;
    this.savedBodyOverflow = body.style.overflow;
    this.savedDocOverscroll = docEl.style.overscrollBehavior;
    this.savedBodyOverscroll = body.style.overscrollBehavior;
    this.savedBodyPaddingRight = body.style.paddingRight;

    // Calculate vertical scrollbar width to prevent layout shift
    const scrollbarWidth = window.innerWidth - docEl.clientWidth;
    if (scrollbarWidth > 0) {
      const currentPadding = parseFloat(window.getComputedStyle(body).paddingRight || '0') || 0;
      body.style.paddingRight = `${currentPadding + scrollbarWidth}px`;

      // Also adjust fixed headers that span full width to prevent header shift
      const fixedHeaders = document.querySelectorAll<HTMLElement>(
        '.fixed.w-full, app-header.fixed, header.fixed, .app-header-fixed'
      );
      this.savedElementsPadding.clear();
      fixedHeaders.forEach(el => {
        const elPadding = parseFloat(window.getComputedStyle(el).paddingRight || '0') || 0;
        this.savedElementsPadding.set(el, el.style.paddingRight);
        el.style.paddingRight = `${elPadding + scrollbarWidth}px`;
      });
    }

    // Lock both root document element and body
    docEl.style.overflow = 'hidden';
    docEl.style.overscrollBehavior = 'none';
    docEl.classList.add('modal-open');

    body.style.overflow = 'hidden';
    body.style.overscrollBehavior = 'none';
    body.classList.add('modal-open');
  }

  private unlockBodyScroll(): void {
    const body = document.body;
    const docEl = document.documentElement;

    docEl.style.overflow = this.savedDocOverflow || '';
    docEl.style.overscrollBehavior = this.savedDocOverscroll || '';
    docEl.classList.remove('modal-open');

    body.style.overflow = this.savedBodyOverflow || '';
    body.style.overscrollBehavior = this.savedBodyOverscroll || '';
    body.style.paddingRight = this.savedBodyPaddingRight || '';
    body.classList.remove('modal-open');

    this.savedElementsPadding.forEach((padding, el) => {
      try {
        el.style.paddingRight = padding || '';
      } catch (e) {}
    });
    this.savedElementsPadding.clear();

    this.savedDocOverflow = '';
    this.savedDocOverscroll = '';
    this.savedBodyOverflow = '';
    this.savedBodyOverscroll = '';
    this.savedBodyPaddingRight = '';
  }

  private attachScrollListeners(): void {
    if (this.scrollListenersAdded) return;
    window.addEventListener('keydown', this.handleKeyDown, true);
    window.addEventListener('wheel', this.handleWheel, { passive: false, capture: true });
    window.addEventListener('touchstart', this.handleTouchStart, { passive: true, capture: true });
    window.addEventListener('touchmove', this.handleTouchMove, { passive: false, capture: true });
    this.scrollListenersAdded = true;
  }

  private detachScrollListeners(): void {
    if (!this.scrollListenersAdded) return;
    window.removeEventListener('keydown', this.handleKeyDown, true);
    window.removeEventListener('wheel', this.handleWheel, { capture: true });
    window.removeEventListener('touchstart', this.handleTouchStart, { capture: true });
    window.removeEventListener('touchmove', this.handleTouchMove, { capture: true });
    this.scrollListenersAdded = false;
  }

  private handleKeyDown = (event: KeyboardEvent): void => {
    if (event.key === 'Escape' || event.key === 'Esc') {
      // 1. Try explicit modal registration from top of stack
      for (let i = this.activeModals.length - 1; i >= 0; i--) {
        const top = this.activeModals[i];
        if (top.closeOnEsc !== false && top.onClose) {
          event.preventDefault();
          event.stopPropagation();
          top.onClose();
          return;
        }
      }

      // 2. If no explicit callback, try triggering close on topmost observed DOM modal
      if (this.observedModalElements.size > 0) {
        const elements = Array.from(this.observedModalElements);
        const topEl = elements[elements.length - 1];
        if (topEl) {
          // Look for close button or backdrop click
          const closeBtn = topEl.querySelector<HTMLElement>(
            'button[aria-label*="close" i], button[title*="close" i], button.close, [data-modal-close], [data-close], button svg[viewBox="0 0 24 24"]'
          );
          if (closeBtn && typeof closeBtn.click === 'function') {
            event.preventDefault();
            event.stopPropagation();
            closeBtn.click();
            return;
          }
          // Try clicking direct backdrop
          const backdrop = topEl.querySelector<HTMLElement>('[class*="bg-black"], [class*="backdrop"]') || topEl;
          if (backdrop && typeof backdrop.click === 'function') {
            event.preventDefault();
            event.stopPropagation();
            backdrop.click();
          }
        }
      }
    } else if (
      event.key === ' ' ||
      event.key === 'Spacebar' ||
      event.key === 'PageDown' ||
      event.key === 'PageUp'
    ) {
      const target = event.target as HTMLElement | null;
      const isInput =
        target &&
        (target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.tagName === 'SELECT' ||
          target.isContentEditable);
      if (!isInput) {
        const scrollable = this.findScrollableContainer(target);
        if (!scrollable && event.cancelable) {
          event.preventDefault();
        }
      }
    }
  };

  private isElementInsideModal(el: any): boolean {
    if (!el || typeof el.closest !== 'function') return false;
    if (this.observedModalElements.size > 0) {
      for (const modalEl of this.observedModalElements) {
        if (typeof modalEl.contains === 'function' && modalEl.contains(el)) return true;
      }
    }
    const modalSelector =
      '[role="dialog"], [role="alertdialog"], .buy-now-popup, .pdp-buy-now-popup, .modal-container, .modal-content, app-buy-now-popup, app-address, app-gst, app-coupon, app-cart, app-filter, app-spare-parts-filter, app-scrap-certificate-modal, app-login, div.fixed.inset-0, div.fixed.inset-y-0, section.fixed';
    return !!el.closest(modalSelector);
  }

  private findScrollableContainer(target: any): HTMLElement | null {
    if (!target || typeof target.closest !== 'function') {
      return null;
    }
    let el: HTMLElement | null = target as HTMLElement;
    const body = document.body;
    const docEl = document.documentElement;

    if (!this.isElementInsideModal(target)) {
      return null;
    }

    while (el && el !== body && el !== docEl) {
      const className = (el.className || '').toString();
      // Pure backdrop overlay is never a scroll container
      if (
        className.includes('bg-black') &&
        (className.includes('opacity') || className.includes('/')) &&
        !className.includes('overflow-y-auto') &&
        !className.includes('overflow-auto')
      ) {
        return null;
      }

      const style = window.getComputedStyle(el);
      const isOverflowY =
        (style.overflowY === 'auto' || style.overflowY === 'scroll') &&
        el.scrollHeight > el.clientHeight;
      const isOverflowX =
        (style.overflowX === 'auto' || style.overflowX === 'scroll') &&
        el.scrollWidth > el.clientWidth;

      if (isOverflowY || isOverflowX) {
        return el;
      }

      if (
        className.includes('buy-now-popup') ||
        className.includes('pdp-buy-now-popup') ||
        el.getAttribute('role') === 'dialog'
      ) {
        break;
      }

      el = el.parentElement;
    }
    return null;
  }

  private handleWheel = (event: WheelEvent): void => {
    if (!this.isLocked) return;

    const target = event.target as HTMLElement | null;
    const scrollable = this.findScrollableContainer(target);

    if (!scrollable) {
      if (event.cancelable) {
        event.preventDefault();
      }
      return;
    }

    const { deltaY, deltaX } = event;
    const isVertical = Math.abs(deltaY) >= Math.abs(deltaX);

    if (isVertical) {
      const { scrollTop, scrollHeight, clientHeight } = scrollable;
      const atTop = deltaY < 0 && scrollTop <= 0;
      const atBottom = deltaY > 0 && scrollTop + clientHeight >= scrollHeight - 1;
      if (atTop || atBottom) {
        if (event.cancelable) {
          event.preventDefault();
        }
      }
    } else {
      const { scrollLeft, scrollWidth, clientWidth } = scrollable;
      const atLeft = deltaX < 0 && scrollLeft <= 0;
      const atRight = deltaX > 0 && scrollLeft + clientWidth >= scrollWidth - 1;
      if (atLeft || atRight) {
        if (event.cancelable) {
          event.preventDefault();
        }
      }
    }
  };

  private handleTouchStart = (event: TouchEvent): void => {
    if (event.touches.length === 1) {
      this.touchStartY = event.touches[0].clientY;
      this.touchStartX = event.touches[0].clientX;
    }
  };

  private handleTouchMove = (event: TouchEvent): void => {
    if (!this.isLocked) return;

    const target = event.target as HTMLElement | null;
    const scrollable = this.findScrollableContainer(target);

    if (!scrollable) {
      if (event.cancelable) {
        event.preventDefault();
      }
      return;
    }

    if (event.touches.length === 1) {
      const currentY = event.touches[0].clientY;
      const currentX = event.touches[0].clientX;
      const deltaY = this.touchStartY - currentY;
      const deltaX = this.touchStartX - currentX;
      const isVertical = Math.abs(deltaY) >= Math.abs(deltaX);

      if (isVertical) {
        const { scrollTop, scrollHeight, clientHeight } = scrollable;
        const atTop = deltaY < 0 && scrollTop <= 0;
        const atBottom = deltaY > 0 && scrollTop + clientHeight >= scrollHeight - 1;
        if (atTop || atBottom) {
          if (event.cancelable) {
            event.preventDefault();
          }
        }
      } else {
        const { scrollLeft, scrollWidth, clientWidth } = scrollable;
        const atLeft = deltaX < 0 && scrollLeft <= 0;
        const atRight = deltaX > 0 && scrollLeft + clientWidth >= scrollWidth - 1;
        if (atLeft || atRight) {
          if (event.cancelable) {
            event.preventDefault();
          }
        }
      }
    }
  };

  /**
   * Initializes a DOM MutationObserver to detect when modal overlay elements
   * are mounted into or removed from the DOM, providing universal fallback coverage.
   */
  private initDomObserver(): void {
    if (typeof MutationObserver === 'undefined') return;

    this.ngZone.runOutsideAngular(() => {
      const checkDomModals = () => {
        const visibleModals: HTMLElement[] = [];
        const candidates = document.querySelectorAll<HTMLElement>(
          'div.fixed.inset-0, [role="dialog"], [role="alertdialog"], .modal-container, .mobile-bottom-sheet'
        );

        candidates.forEach(el => {
          if (this.isModalOverlayElement(el)) {
            visibleModals.push(el);
          }
        });

        // Update observed set
        let changed = false;
        const currentElements = new Set(visibleModals);

        for (const el of this.observedModalElements) {
          if (!currentElements.has(el)) {
            this.observedModalElements.delete(el);
            changed = true;
          }
        }

        for (const el of visibleModals) {
          if (!this.observedModalElements.has(el)) {
            this.observedModalElements.add(el);
            changed = true;
          }
        }

        if (changed) {
          this.syncScrollLockState();
        }
      };

      this.mutationObserver = new MutationObserver(() => {
        if (this.rafId === null) {
          this.rafId = window.requestAnimationFrame(() => {
            this.rafId = null;
            checkDomModals();
          });
        }
      });

      this.mutationObserver.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['class', 'style', 'hidden']
      });

      // Initial check
      checkDomModals();
    });
  }

  private isModalOverlayElement(el: HTMLElement): boolean {
    // Ignore elements inside headers, navbars, toast, or notifications
    if (
      el.closest('header, app-header, app-otc-header, app-notification, app-toast, .toast-container, .notification-container')
    ) {
      return false;
    }

    if (el.hasAttribute('data-no-scroll-lock')) {
      return false;
    }

    if (el.hidden || el.style.display === 'none') {
      return false;
    }

    const style = window.getComputedStyle(el);
    if (style.display === 'none' || style.visibility === 'hidden') {
      return false;
    }

    // Check for visibility: hidden / invisible
    if (el.classList.contains('invisible') || el.classList.contains('hidden')) {
      return false;
    }

    // Check for opacity 0
    if (
      style.opacity === '0' ||
      parseFloat(style.opacity || '1') === 0 ||
      el.classList.contains('opacity-0')
    ) {
      return false;
    }

    // Check for pointer-events: none (inactive overlays)
    if (
      style.pointerEvents === 'none' ||
      el.classList.contains('pointer-events-none')
    ) {
      return false;
    }

    if (style.position !== 'fixed' && style.position !== 'absolute') {
      return false;
    }

    const rect = el.getBoundingClientRect();

    // Check if element is transformed completely off-screen or has zero dimensions
    if (
      rect.width === 0 ||
      rect.height === 0 ||
      rect.bottom <= 0 ||
      rect.top >= window.innerHeight ||
      rect.right <= 0 ||
      rect.left >= window.innerWidth
    ) {
      return false;
    }

    const coversSignificantArea =
      (rect.width >= window.innerWidth * 0.7 && rect.height >= window.innerHeight * 0.7) ||
      (rect.width >= window.innerWidth * 0.7 && rect.bottom >= window.innerHeight * 0.8) ||
      (rect.height >= window.innerHeight * 0.7 && rect.right >= window.innerWidth * 0.8);

    if (!coversSignificantArea) {
      return false;
    }

    const hasRole = el.getAttribute('role') === 'dialog' || el.getAttribute('role') === 'alertdialog';
    const className = (el.className || '').toString().toLowerCase();
    const hasBackdropOrModal =
      className.includes('modal') ||
      className.includes('popup') ||
      className.includes('backdrop') ||
      className.includes('overlay') ||
      className.includes('inset-0') ||
      (className.includes('bg-black') && (className.includes('opacity') || className.includes('/')));

    return hasRole || hasBackdropOrModal;
  }
}

